import { useMemo, useState } from "react"
import { Plus, Search } from "lucide-react"
import {
  IconSidebarHiddenLeftWide,
  IconSidebarSimpleLeftWide,
} from "central-icons-outline"
import { AriIcon } from "@/components/sidebar/icons"
import { Button } from "@/components/ui/Button"
import { cn } from "@/lib/utils"
import { AppChatComposer } from "@/components/ui/app-chat/AppChatComposer"
import { AppChatConversation } from "@/components/ui/app-chat/AppChatConversation"
import { AppChatSessionList } from "@/components/ui/app-chat/AppChatSessionList"
import { useChatPanel } from "@/contexts/ChatPanelContext"
import { ChatScope, type SuggestedPrompt } from "@/types/chat.types"
import { AriHomeEmptyState } from "./components/AriHomeEmptyState"

const suggestedPrompts: SuggestedPrompt[] = [
  { label: "Summarize my active deals", scope: ChatScope.Global },
  { label: "What are the key terms in my recording contract?", scope: ChatScope.Document },
  { label: "Show me overdue deliverables", scope: ChatScope.Global },
  { label: "Compare my deal pipeline values", scope: ChatScope.Deal },
]

const ChatPage = () => {
  const {
    sessions,
    currentSessionId,
    currentSession,
    selectSession,
    deleteSession,
    createSession,
    messages,
    currentDraft,
    setCurrentDraft,
    currentScope,
    setCurrentScope,
    contextItems,
    setContextItems,
    removeContextItem,
    dynamicPageItems,
    sendMessage,
    isCurrentSessionLoading,
    settings,
    openPanel,
  } = useChatPanel()

  const [searchQuery, setSearchQuery] = useState("")
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const isHome = messages.length === 0

  const filteredSessions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) {
      return sessions
    }

    return sessions.filter((session) =>
      session.title.toLowerCase().includes(query)
      || session.messages.some((message) => message.content.toLowerCase().includes(query)),
    )
  }, [searchQuery, sessions])

  const handlePromptSelect = (prompt: SuggestedPrompt) => {
    setCurrentScope(prompt.scope)
    sendMessage(prompt.label, { scope: prompt.scope })
  }

  const composerProps = {
    draft: currentDraft,
    onDraftChange: setCurrentDraft,
    onSubmit: () => sendMessage(),
    isLoading: isCurrentSessionLoading,
    scope: currentScope,
    onScopeChange: setCurrentScope,
    contextItems,
    onContextItemsChange: setContextItems,
    onRemoveContextItem: removeContextItem,
    extraContextItems: dynamicPageItems,
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <aside
          className={cn(
            "flex shrink-0 flex-col overflow-hidden border-r border-border/50 bg-muted/20 transition-[width] duration-300 ease-in-out",
            isSidebarOpen ? "w-64" : "w-10",
          )}
          aria-label="Chat history"
          aria-expanded={isSidebarOpen}
        >
          <div
            className={cn(
              "shrink-0 p-3 transition-[padding] duration-300 ease-in-out",
              isSidebarOpen
                ? "flex items-center gap-2"
                : "flex flex-col items-center gap-2 px-1.5",
            )}
          >
            {isSidebarOpen ? (
              <>
                <Button
                  variant="outline"
                  className="flex-1 justify-start gap-2 border-border/60 bg-background text-sm shadow-none"
                  onClick={() => createSession(currentScope)}
                >
                  <Plus className="size-4" />
                  New chat
                </Button>
                <Button
                  variant="outline"
                  size="icon-sm"
                  aria-label="Hide chat history"
                  onClick={() => setIsSidebarOpen(false)}
                >
                  <IconSidebarHiddenLeftWide size={16} ariaHidden />
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                size="icon-sm"
                aria-label="Show chat history"
                onClick={() => setIsSidebarOpen(true)}
              >
                <IconSidebarSimpleLeftWide size={16} ariaHidden />
              </Button>
            )}
          </div>

          {isSidebarOpen && (
            <>
              <div className="px-3 pb-3">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search chats..."
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    className="w-full rounded-xl border border-border/40 bg-background py-1.5 pl-8 pr-3 text-xs text-foreground outline-none focus:border-primary/30"
                  />
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
                <AppChatSessionList
                  sessions={filteredSessions}
                  currentSessionId={currentSessionId}
                  onSelectSession={selectSession}
                  onDeleteSession={deleteSession}
                  variant="page"
                />
              </div>
            </>
          )}
        </aside>

        <section className="flex min-w-0 flex-1 flex-col bg-[#f9f9f9] dark:bg-background">
          {isHome ? (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-y-auto px-4 py-8">
              <AriHomeEmptyState
                suggestedPrompts={suggestedPrompts}
                onPromptSelect={handlePromptSelect}
                composer={(
                  <AppChatComposer
                    {...composerProps}
                    variant="home"
                    placeholder="Ask about deals, contracts, or your roster…"
                  />
                )}
              />
            </div>
          ) : (
            <>
              <div className="border-b border-border/40 bg-background/80 px-6 py-3.5 backdrop-blur-sm">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <AriIcon variant="gradient" className="size-4 shrink-0" aria-hidden="true" />
                    <h1 className="truncate text-base font-medium">
                      {currentSession.title}
                    </h1>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={openPanel}>
                    Open app chat
                  </Button>
                </div>
              </div>

              <AppChatConversation
                messages={messages}
                settings={settings}
                isLoading={isCurrentSessionLoading}
                variant="page"
              />

              <AppChatComposer
                {...composerProps}
                placeholder="Reply to Ari..."
                variant="page"
              />
            </>
          )}
        </section>
      </div>
    </div>
  )
}

export default ChatPage
